import { Button } from '@material-ui/core';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import {
  DataFields,
  FileUpload,
  UnprocessableFields,
} from 'api-clients/monolith';
import cx from 'classnames';
import React, { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { DuplicateApplicantModal } from 'components/DuplicateApplicantModal';
import { ReachJobDescription } from 'components/JobDescription';
import { LoadingBoundary } from 'components/LoadingBoundary';
import { ReachOpening } from 'components/Opening';
import { Requirements } from 'components/Requirements';
import { Snackbar } from 'components/Snackbar';
import { Typography } from 'components/Typography';
import {
  PreambleProps,
  ProgressManager,
  SubmitProps,
} from 'features/DataCollection/ProgressManager';
import { domainMeta } from 'utils/domainMeta';

import { DuplicateApplicantDataType } from './constants';
import { RocketLaunchIcon } from './RocketLaunchIcon';
import { useStyles } from './styles';

import type {
  Opening as OpeningType,
  SubmitStatus,
} from './ApplicationFormBodyRoute';

export interface ApplicationFormBodyProps {
  ApplicationFormQuestionsComponent: React.ComponentType<PreambleProps>;
  canReapply: boolean;
  className?: string;
  duplicateApplicantData?: DuplicateApplicantDataType;
  handleDuplicateApplicantModalClose: () => void;
  handleReapply: () => Promise<void>;
  handleStartApplication: () => void;
  jobDescriptionHTML: string;
  opening: OpeningType;
  sendPreviousApplicationLink: () => Promise<void>;
  setSubmitting: React.Dispatch<React.SetStateAction<SubmitStatus>>;
  Submit: React.VFC<SubmitProps>;
  submitErrorMessage: (validation?: UnprocessableFields) => JSX.Element;
  submitting: SubmitStatus;
  handleSubmit: (
    formData: FormData,
    event?: FormEvent<Element>,
  ) => Promise<void>;
  dataFields: DataFields;
  formName: (dataKey: string) => string;
  uploadFields?: FileUpload;
  accountSlug: string;
  handleBackToTop: () => void;
  showAllJobsButton: boolean;
  hideJobWording: boolean;
  isDspBrand?: boolean;
  isItcDemoEnabled: boolean;
  requirements?: string;
  isExpandJobDescription: boolean;
  showApplyStickyButton?: boolean;
  showCompensation: boolean;
  directoryPath: string;
}

export const ReachApplicationFormBody: React.FC<ApplicationFormBodyProps> = ({
  ApplicationFormQuestionsComponent,
  canReapply,
  accountSlug,
  className,
  duplicateApplicantData,
  handleDuplicateApplicantModalClose,
  handleReapply,
  handleStartApplication,
  jobDescriptionHTML,
  opening,
  sendPreviousApplicationLink,
  setSubmitting,
  Submit,
  submitErrorMessage,
  submitting,
  handleSubmit,
  dataFields,
  formName,
  uploadFields,
  handleBackToTop,
  showAllJobsButton,
  hideJobWording,
  isDspBrand,
  isItcDemoEnabled,
  requirements,
  isExpandJobDescription,
  showCompensation,
  directoryPath,
}) => {
  const styles = useStyles();
  const intl = useIntl();

  const submitError = submitting.status === 'error';

  const targetRef = useRef<HTMLDivElement>(null);
  const [showApplyStickyButton, setShowApplyStickyButton] =
    useState<boolean>(false);

  useEffect(() => {
    const currentTarget = targetRef.current;

    // Hide Apply button when the form is in view and show when it is not
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          setShowApplyStickyButton(!entry.isIntersecting);
        });
      },
      { threshold: 0.1 },
    );

    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, []);

  const jobTitle = useMemo(() => {
    if (isItcDemoEnabled)
      return intl.formatMessage({
        defaultMessage: 'Welcome',
        description: 'Job Description Section Header',
      });

    if (hideJobWording)
      return intl.formatMessage({
        defaultMessage: 'Description',
        description: 'Opening Description Section Header',
      });

    return intl.formatMessage({
      defaultMessage: 'Job Description',
      description: 'Job Description Section Header',
    });
  }, [hideJobWording, intl, isItcDemoEnabled]);

  const jobAtAGlance =
    typeof requirements === 'string' && requirements !== ''
      ? (JSON.parse(requirements) as string[])
      : requirements;

  return (
    <LoadingBoundary
      loading={submitting.status === 'loading'}
      className={className}
    >
      {duplicateApplicantData && (
        <DuplicateApplicantModal
          handleClose={handleDuplicateApplicantModalClose}
          {...duplicateApplicantData}
          handleCancel={canReapply ? handleReapply : undefined}
          handleSubmit={sendPreviousApplicationLink}
        />
      )}
      <div className={styles.columnLayout}>
        <Snackbar
          open={submitError}
          severity="error"
          onClose={() => setSubmitting({ status: 'idle' })}
          message={submitErrorMessage(
            submitError ? submitting.error : undefined,
          )}
        />
        <ReachOpening {...opening} showCompensation={showCompensation} />
        {jobAtAGlance && jobAtAGlance?.length > 0 && (
          <Requirements requirements={jobAtAGlance} />
        )}
        {jobDescriptionHTML && (
          <ReachJobDescription
            title={jobTitle}
            body={jobDescriptionHTML}
            isExpandJobDescription={isExpandJobDescription}
          />
        )}
        <div
          ref={targetRef}
          className={cx(styles.columnLayout, styles.reachDesktopBorder)}
        >
          <div className={styles.title}>
            <RocketLaunchIcon />
            <Typography variant="headingSM">
              {isItcDemoEnabled ? (
                <FormattedMessage
                  defaultMessage="Start Your Journey"
                  description="Start Your Application Section Header"
                />
              ) : (
                <FormattedMessage
                  defaultMessage="Start Your Application"
                  description="Start Your Application Section Header"
                />
              )}
            </Typography>
          </div>

          <ProgressManager
            Preamble={ApplicationFormQuestionsComponent}
            Submit={Submit}
            onSubmit={handleSubmit}
            dataFields={dataFields}
            inputName={formName}
            uploadFields={uploadFields}
            countryCode={opening.countryCode}
            serverInvalidFields={submitError ? submitting.error : undefined}
            isReachApplicationFormEnabled
          />
        </div>
        {showAllJobsButton && (
          <Button
            href={
              isDspBrand
                ? `${domainMeta.monolithOrigin}/jobs/${accountSlug}`
                : directoryPath
            }
            variant="outlined"
          >
            {hideJobWording ? (
              <FormattedMessage
                defaultMessage="View all"
                description="Link to view all listings"
              />
            ) : (
              <FormattedMessage
                defaultMessage="View all jobs"
                description="Link to view all job listings"
              />
            )}
          </Button>
        )}
        <Button
          variant="outlined"
          startIcon={<ArrowUpwardIcon />}
          onClick={handleBackToTop}
        >
          <FormattedMessage
            defaultMessage="Back to top"
            description="Button to scroll to top of page"
          />
        </Button>
      </div>
      {showApplyStickyButton && (
        <div className={styles.applyWrapper}>
          <Typography variant="subtitle" className={styles.applyText}>
            {opening.title} • {opening.brandName}
          </Typography>
          <Button
            className={styles.button}
            onClick={handleStartApplication}
            variant="contained"
            color="primary"
          >
            {isItcDemoEnabled ? (
              <FormattedMessage
                defaultMessage="Start Your Journey"
                description="Start Application button for the Application Form"
              />
            ) : (
              <FormattedMessage
                defaultMessage="Apply"
                description="Start Application button for the Application Form"
              />
            )}
          </Button>
        </div>
      )}
    </LoadingBoundary>
  );
};
